'use client';

import { useState, useEffect } from 'react';
import { saveCheckoutMeta } from '../checkout';
import { FaCreditCard } from "react-icons/fa6";
import { useSession } from "next-auth/react";
import { useTranslations, useLocale } from 'next-intl';

interface BuyButtonProps {
  priceId?: string;
  customData?: Record<string, any>;
  className?: string;
  children: React.ReactNode;
}

const localeOfferPaths: Record<string, string> = {
  en: '/account/offer',
  fr: '/compte/offer',
  ar: '/الحساب/العروض',
  nl: '/account/offers',
  de: '/konto/angebote',
  pt: '/conta/ofertas',
  sv: '/konto/erbjudanden',
  ru: '/аккаунт/предложения',
  it: '/account/offerte',
  es: '/cuenta/ofertas',
};

const localeSubdomains: Record<string, string> = {
  en: 'padelmasterthegame.com',
  fr: 'fr.padelmasterthegame.com',
  ar: 'ar.padelmasterthegame.com',
  it: 'it.padelmasterthegame.com',
  ru: 'ru.padelmasterthegame.com',
  de: 'de.padelmasterthegame.com',
  sv: 'sv.padelmasterthegame.com',
  nl: 'nl.padelmasterthegame.com',
  es: 'es.padelmasterthegame.com',
  pt: 'pt.padelmasterthegame.com',
};

export default function PaymentCard({
  priceId,
  customData,
  className = "text-center text-secondary flex justify-center gap-1 md:gap-3 font-medium w-80 md:w-96 hover:bg-a hover:text-white items-center border-1 border-a rounded-xl px-2 md:px-4 py-4 transition-all duration-300 ease-in-out cursor-pointer bg-b backdrop-blur-sm hover:scale-110 hover:shadow-[0_0_35px_rgba(255,255,255,0.65)] active:scale-95 animate-pulse-ring",
  children
}: BuyButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError]         = useState<string>('');
  const { data: session, status } = useSession();
  const th                        = useTranslations('Padel-Master-The-Game-Home-Page');
  const locale                    = useLocale();
  const [affiliateCode, setAffiliateCode] = useState<string | null>(null);

  // ✅ Read localStorage in useEffect — safe for SSR
  useEffect(() => {
    const code = localStorage.getItem('affiliate');
    setAffiliateCode(code);
  }, []);

  const finalPriceId = priceId || process.env.NEXT_PUBLIC_PADDLE_PRICE_ID;

  const handlePurchase = async () => {
    if (!finalPriceId) {
      setError('Price ID not configured');
      return;
    }

    if (!window.Paddle) {
      setError(th("e1"));
      return;
    }

    setIsLoading(true);
    setError('');

    const userEmail    = session?.user?.email               ?? null;
    const userFullName = (session?.user as any)?.full_name  ?? null;
    const userId       = (session?.user as any)?.id         ?? null;

    const domain     = localeSubdomains[locale] ?? 'padelmasterthegame.com';
    const path       = localeOfferPaths[locale] ?? '/account/offer';
    const successUrl = `https://${domain}${path}`;

    try {
      window.Paddle.Checkout.open({
        settings: { successUrl },
        items: [{ priceId: finalPriceId, quantity: 1 }],
        customer: { email: userEmail },   // ✅ prefills email
        customData: {
          ...customData,
          userId,
          affiliate_code: affiliateCode,
          email_user:     userEmail,
          name_user:      userFullName,
          language:       locale,
        },
      });

      if (userId) {
        await saveCheckoutMeta({
          userId,
          email:         userEmail,
          name:          userFullName,
          language:      locale,
          affiliateCode,
        });
      }

    } catch (err: any) {
      console.error('Purchase error:', err);
      setError(err.message || th("e3"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes pulse-ring {
          0% { box-shadow: 0 0 0 0px rgba(255,255,255,0.45); }
          100% { box-shadow: 0 0 0 18px rgba(255,255,255,0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes fadeSlideUp {
          0% { opacity: 0; transform: translateY(40px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .btn-card { animation: pulse-ring 2s ease-out infinite; }
        .btn-crypto { animation: pulse-ring 2s ease-out infinite 1s; }
        .book-float { animation: float 4s ease-in-out infinite; }
        .fade-up-1 { animation: fadeSlideUp 0.8s ease-out 0.2s both; }
        .fade-up-2 { animation: fadeSlideUp 0.8s ease-out 0.5s both; }
        .fade-up-3 { animation: fadeSlideUp 0.8s ease-out 0.8s both; }
        .pay-btn {
          border: 4px solid white;
          color: white;
          border-radius: 9999px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 8px;
          background: rgba(0,0,0,0.4);
          backdrop-filter: blur(8px);
          transition: all 0.3s ease-in-out;
          cursor: pointer;
        }
        .pay-btn:hover {
          background: white;
          color: black;
          transform: scale(1.12);
          box-shadow: 0 0 35px rgba(255,255,255,0.65);
        }
        .pay-btn:active { transform: scale(0.95); }
        .pay-btn:hover svg { color: black; }
      `}</style>

      <div>
        <button
          onClick={handlePurchase}
          disabled={isLoading || !finalPriceId}
          className={className}
        >
          <FaCreditCard className="inline-block mr-3 mb-1" size={24} />
          <p className="m-0 text-sm md:text-base font-bold leading-tight text-center px-1">
            {isLoading ? th("Processing") + "..." : children}
          </p>
        </button>

        {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
      </div>
    </>
  );
}